import { AchievementInfos } from '@interfaces/Achievements'
import { CertificateTypes } from '@utils/constants'
import groupBy from 'lodash/groupBy'
import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { StudentProfileInfo } from '@interfaces/Student'
import { flatten } from 'lodash'

const levelRegEx = /[0-9]{2}|[0-9]/ // material language level is currently derived from material name
class Certificates {
  data: AchievementInfos[]
  studentProfile: StudentProfileInfo

  constructor(data: AchievementInfos[], studentProfile: StudentProfileInfo) {
    this.studentProfile = studentProfile
    this.data = this.initializeRegistrationAttributes(data)
  }

  private initializeRegistrationAttributes(data) {
    const registrations = this.studentProfile?.RegistrationWrappers
    return data.map((cert) => {
      const registration = registrations?.find(
        (reg) => reg.RegistrationInfo.RegistrationId === cert.achievementInfo?.Registration?.Id
      )
      return {
        ...cert,
        achievementInfo: {
          ...cert.achievementInfo,
          Registration: {
            ...cert.achievementInfo?.Registration,
            Language: registration?.RegistrationInfo?.Language,
            LanguageLevel: Number(registration?.RegistrationInfo?.LanguageLevel ?? 0),
            PartialName:
              cert.achievementInfo?.Registration?.RegistrationMaterials?.[0]?.MaterialName?.split(levelRegEx)?.[0],
            // ProgramType: registration?.RegistrationInfo?.ProgramType,
          },
        },
      }
    })
  }

  get GroupedCertificatesInit() {
    let data = groupBy(this.data, (item) => item.achievementInfo?.Registration?.Id)

    /**
     * Sort by program start date
     */
    data = Object.entries(data)
      .sort(([, a], [, b]) => {
        const startDateA = moment(a[0].achievementInfo?.Registration?.Program?.StartDate)
        const startDateB = moment(b[0].achievementInfo?.Registration?.Program?.StartDate)
        return startDateB.diff(startDateA)
      })
      .reduce((r, [k, v]) => ({ ...r, [k]: v }), {})

    return Object.keys(data).map((key) => {
      return data[key]
        .sort((a, b) => (a.achievementInfo?.TemplateName || '').localeCompare(b.achievementInfo?.TemplateName || ''))
        .map((cert) => {
          const temp = cert.achievementInfo

          const startDate = moment(temp?.Registration?.Program?.StartDate)
          const endDate = moment(temp?.Registration?.Program?.EndDate)

          let date = ''
          if (!startDate.isSame(endDate, 'year')) {
            date = `${startDate.format('Do MMMM YYYY')} - ${endDate.format('Do MMMM YYYY')}`
          } else {
            date = `${startDate.format('Do MMMM')} - ${endDate.format('Do MMMM YYYY')}`
          }

          return {
            date,
            id: temp?.Id,
            registrationId: temp?.Registration?.Id,
            coreName:
              temp?.Registration?.RegistrationMaterials?.filter(
                (material) => material.MaterialName && !/MyBerlitz.*Tutor/.test(material.MaterialName)
              )?.find((mat) => !mat.IsElective && !mat.IsTest)?.MaterialName ||
              temp?.Registration?.RegistrationMaterials?.find(
                (material) => material.MaterialName && !/MyBerlitz.*Tutor/.test(material.MaterialName)
              )?.MaterialName,
            type: temp?.Type,
            templateName: temp?.TemplateName === 'Curriculum Test Passed' ? 'Curriculum Test - Passed' : undefined,
            name: `${temp?.StudentProfile?.FirstName || ''}  ${temp?.StudentProfile?.LastName}`,
            materials: temp?.Registration?.RegistrationMaterials?.filter((reg) => !reg.IsTest)
              ?.map((reg) => reg.MaterialName)
              ?.filter((materialName) => materialName && !/MyBerlitz.*Tutor/.test(materialName)),
            signedBy: {
              title: temp?.SignedBy?.DisplayTitle || '',
              signature: temp?.SignedBy?.DisplaySignatureURL || '',
              name: temp?.SignedBy?.DisplayName || '',
            },
            link: `${process.env.CERTIFICATE_URL}?id=${temp?.ReferenceId}`,
            linkLabel: `${process.env.CERTIFICATE_URL?.replace('https://', '')}?id=${temp?.ReferenceId}`,
            description1: temp?.Type === CertificateTypes.ACHIEVEMENT ? 'Berlitz certifies that' : '',
            description2:
              temp?.Type === CertificateTypes.ACHIEVEMENT
                ? 'successfully completed the following program'
                : 'participated in the following program',
            registration: temp?.Registration,
          }
        })
    })
  }

  get GroupedCertificates() {
    /**
     * Group by program start date, level, language and its registration id:
     */
    let achievementsByDateAndLanguage = groupBy(
      this.GroupedCertificatesInit,
      (item) =>
        `${item?.[0]?.registration?.Program?.StartDate}_${item?.[0]?.registration?.Language}_${item?.[0]?.registration?.LanguageLevel}_${item?.[0]?.registrationId}`
    )

    /**
     * Lets convert it into an array of objects so that
     * we can sort it in descending order
     */
    let achievementsInKeyPairs = Object.keys(achievementsByDateAndLanguage).map((key) => ({
      key,
      value: achievementsByDateAndLanguage[key],
    }))
    achievementsInKeyPairs = orderBy(achievementsInKeyPairs, 'key', 'desc')

    return achievementsInKeyPairs
      .map((item) => {
        const certificates = item.value

        return {
          key: item.key,
          value: flatten(certificates),
        }
      })
      .reduce((acc, cur) => {
        acc.push(cur.value)
        return acc
      }, [] as any)
  }
}

export default Certificates
